import React from "react";
import { api } from "@/lib/config";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Phone, MapPin, Globe, Loader2 } from "lucide-react";

export default function Contact() {
  const [form, setForm] = React.useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message sent. Thank you.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send.");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="contact-page" className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <div className="grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="eyebrow text-[color:var(--brand-wood)]">Get in touch</div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight">
            Let&apos;s talk about your floors.
          </h1>
          <p className="mt-4 text-[color:var(--brand-woodDark)]">
            Prefer email or phone? We answer both. Or leave a message and we&apos;ll reply within one business day.
          </p>

          <ul className="mt-10 space-y-4 text-sm">
            <li className="flex items-center gap-3">
              <span className="h-9 w-9 rounded-full bg-[color:var(--brand-stone)] flex items-center justify-center"><Mail className="h-4 w-4 text-[color:var(--brand-woodDark)]"/></span>
              <a href="mailto:info@jfeva.com" data-testid="contact-email-link" className="hover:underline">info@jfeva.com</a>
            </li>
            <li className="flex items-center gap-3">
              <span className="h-9 w-9 rounded-full bg-[color:var(--brand-stone)] flex items-center justify-center"><Phone className="h-4 w-4 text-[color:var(--brand-woodDark)]"/></span>
              <a href="tel:8502520162" data-testid="contact-phone-link" className="hover:underline">850-252-0162</a>
            </li>
            <li className="flex items-center gap-3">
              <span className="h-9 w-9 rounded-full bg-[color:var(--brand-stone)] flex items-center justify-center"><MapPin className="h-4 w-4 text-[color:var(--brand-woodDark)]"/></span>
              <span>Florida &amp; surrounding areas</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="h-9 w-9 rounded-full bg-[color:var(--brand-stone)] flex items-center justify-center"><Globe className="h-4 w-4 text-[color:var(--brand-woodDark)]"/></span>
              <a href="https://jfeva.com" target="_blank" rel="noreferrer" data-testid="contact-website-link" className="hover:underline">jfeva.com</a>
            </li>
          </ul>
        </div>

        <div className="md:col-span-7">
          {done ? (
            <div data-testid="contact-success" className="rounded-3xl border border-black/10 bg-white p-10">
              <h2 className="font-display text-3xl">Message received.</h2>
              <p className="mt-3 text-[color:var(--brand-woodDark)]">We&apos;ll get back to you shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl border border-black/10 bg-white p-8 lg:p-10 grid gap-5 sm:grid-cols-2" data-testid="contact-form">
              <Field label="Name" htmlFor="c-name">
                <Input id="c-name" required data-testid="contact-name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}/>
              </Field>
              <Field label="Email" htmlFor="c-email">
                <Input id="c-email" type="email" required data-testid="contact-emailinput" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})}/>
              </Field>
              <Field label="Phone (optional)" htmlFor="c-phone" className="sm:col-span-2">
                <Input id="c-phone" data-testid="contact-phone" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})}/>
              </Field>
              <Field label="Message" htmlFor="c-message" className="sm:col-span-2">
                <Textarea id="c-message" required rows={6} data-testid="contact-message" value={form.message} onChange={(e)=>setForm({...form,message:e.target.value})}/>
              </Field>
              <div className="sm:col-span-2 flex items-center justify-end">
                <button type="submit" disabled={submitting} data-testid="contact-submit"
                  className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-ink)] text-[color:var(--brand-cream)] px-6 py-3 text-sm font-medium disabled:opacity-60 hover:-translate-y-0.5 transition-transform">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin"/> : null}
                  Send message
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, htmlFor, className = "" }) {
  return (
    <div className={className}>
      <Label htmlFor={htmlFor} className="text-xs uppercase tracking-widest text-[color:var(--brand-woodDark)]">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
